import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  checkboxClass,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getStDischargeNoteForm,
  updateStDischargeNoteForm,
} from "../../../../../services/patientService";
import {
  AlertCircleIcon,
  BrainIcon,
  CommentsIcon,
  FileCheckIcon,
  GraduationCapIcon,
  HeartPulseIcon,
  LogOutIcon,
  MedicalNotesIcon,
  PhoneIcon,
  TargetIcon,
  UtensilsIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  ST_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import PainScalePills from "../ptNotes/PainScalePills";
import {
  COGNITIVE_AREA_OPTIONS,
  COGNITIVE_MENTAL_OPTIONS,
  COMMUNICATION_EXPRESSION_OPTIONS,
  DISCHARGE_DISPOSITION_OPTIONS,
  DISCHARGE_REASON_OPTIONS,
  EDUCATION_TOPIC_OPTIONS,
  FUNCTIONAL_ABILITY_OPTIONS,
  FUNCTIONAL_ABILITY_ROWS,
  GOAL_STATUS_OPTIONS,
  HEARING_STATUS_OPTIONS,
  HEP_COMPLIANCE_OPTIONS,
  LANGUAGE_STATUS_OPTIONS,
  LIQUIDS_CONSISTENCY_OPTIONS,
  ORAL_MOTOR_OPTIONS,
  PAIN_IMPACT_OPTIONS,
  SOLIDS_CONSISTENCY_OPTIONS,
  SPEECH_INTELLIGIBILITY_OPTIONS,
  SPEECH_PRODUCTION_STATUS_OPTIONS,
  ST_DISCHARGE_ARRAY_KEYS,
  ST_GOAL_ROWS,
  ST_INTERVENTION_OPTIONS,
  SWALLOWING_STATUS_OPTIONS,
  SWALLOW_PRECAUTION_OPTIONS,
  emptyStDischargePayload,
  mergeFunctionalAbility,
  mergeGoalStatuses,
  toggleInList,
  today,
} from "./stDischargeNoteContent";
import {
  assertPtVisitMetaForFlag,
  buildPtVisitMetaProps,
  pickPtVisitMeta,
} from "../ptNotes/ptVisitCommon";
import StNotesCommonHeader from "./StNotesCommonHeader";
import {
  normalizeStCommonHeaderState,
  resolveStNotesCommonHeader,
  withNormalizedStCommonHeader,
} from "./stNotesCommonHeaderUtils";

const DATE_MAX = new Date().toISOString().slice(0, 10);

const tableHeadClass =
  "bg-indigo-50/70 text-[9px] font-semibold uppercase tracking-wide text-indigo-700";

const normalizePainLevel = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 && n <= 10 ? n : null;
};

const emptyValues = () => ({
  ...emptyStDischargePayload(),
  dischargeDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const PillToggle = ({ name, options, value, canEdit, onChange }) => (
  <div
    className="flex flex-wrap gap-1.5"
    role="radiogroup"
    aria-label={name}
  >
    {options.map((opt) => {
      const selected = value === opt;
      return (
        <button
          key={opt}
          type="button"
          disabled={!canEdit}
          aria-pressed={selected}
          onClick={() => onChange(selected ? "" : opt)}
          className={`h-7 px-2.5 rounded-md border text-[11px] font-semibold whitespace-nowrap transition-colors ${
            selected
              ? "border-indigo-600 bg-indigo-600 text-white"
              : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
          } ${!canEdit ? "cursor-default opacity-80" : "cursor-pointer"}`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

PillToggle.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const ChipMulti = ({ options, values, canEdit, onToggle }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((opt) => {
      const selected = (values || []).includes(opt);
      return (
        <button
          key={opt}
          type="button"
          disabled={!canEdit}
          aria-pressed={selected}
          onClick={() => onToggle(opt)}
          className={`h-7 px-2.5 rounded-md border text-[11px] font-semibold whitespace-nowrap transition-colors ${
            selected
              ? "border-indigo-600 bg-indigo-600 text-white"
              : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
          } ${!canEdit ? "cursor-default opacity-80" : "cursor-pointer"}`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

ChipMulti.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  values: PropTypes.arrayOf(PropTypes.string),
  canEdit: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

const SelectField = ({
  label,
  name,
  value,
  options,
  canEdit,
  printMode,
  onChange,
  placeholder = "Select…",
}) => (
  <Field label={label}>
    {printMode ? (
      <div className={`${fieldClass} flex items-center`}>{value || "—"}</div>
    ) : (
      <select
        name={name}
        className={fieldClass}
        value={value || ""}
        disabled={!canEdit}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    )}
  </Field>
);

SelectField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

const StDischargeNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyStDischargePayload();
      const loadedPayload =
        data.payload &&
        typeof data.payload === "object" &&
        !Array.isArray(data.payload)
          ? data.payload
          : {};

      const arrays = {};
      ST_DISCHARGE_ARRAY_KEYS.forEach((key) => {
        arrays[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key]
          : base[key];
      });

      setValues({
        ...base,
        ...loadedPayload,
        ...arrays,
        ...pickPtVisitMeta(loadedPayload, data),
        dischargeDate:
          data.dischargeDate || loadedPayload.dischargeDate || today(),
        commonHeader: normalizeStCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        functionalAbility: mergeFunctionalAbility(
          loadedPayload.functionalAbility,
        ),
        goalStatuses: mergeGoalStatuses(loadedPayload.goalStatuses),
        painLevel: normalizePainLevel(loadedPayload.painLevel),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      });
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveStNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Speech Therapy Discharge Note",
            formType:
              clinicalForm.formType || draft?.formType || "st_discharge_note",
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            dischargeDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...emptyStDischargePayload(),
              commonHeader,
            },
          });
          return;
        }

        const res = await getStDischargeNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveStNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...existingPayload, commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleArray = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        ST_FORM_EDITOR_ROLES,
        "View only — only ST can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const { caregiverSlot, filledByUserId, assertCaregiverForCreate } =
    useFormCaregiverPicker({
      patientId,
      formType:
        form?.formType ||
        draft?.formType ||
        clinicalForm?.formType ||
        "st_discharge_note",
      role,
      canEdit,
      preferredStaffId: draft?.visit?.staffId ?? null,
      currentFilledByUserId: form?.filledBy || clinicalForm?.filledBy || null,
    });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      dischargeDate,
      ...stFields
    } = values;

    const nestedPayload = withNormalizedStCommonHeader({
      ...stFields,
      dischargeDate: dischargeDate ? formatDateForInput(dischargeDate) : "",
      painLevel: normalizePainLevel(values.painLevel),
      functionalAbility: mergeFunctionalAbility(values.functionalAbility),
      goalStatuses: mergeGoalStatuses(values.goalStatuses),
    });

    const payload = {
      flag,
      dischargeDate: dischargeDate ? formatDateForInput(dischargeDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    const visitMetaError = assertPtVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateStDischargeNoteForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.dischargeDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const functional = values.functionalAbility || {};
  const goals = values.goalStatuses || {};

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            {form.formName || "Speech Therapy Discharge Note"}
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Comprehensive clinical evaluation for discharge of outpatient ST
            services.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Discharge Date"
          dateValue={displayDateUS(values.dischargeDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400 leading-none">
                  Discharge Date
                </div>
                <InputDatePicker
                  name="dischargeDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.dischargeDate) || ""}
                  onChange={(event) =>
                    setValue("dischargeDate", event.target.value)
                  }
                  max={DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildPtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <StNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <Section icon={AlertCircleIcon} title="7. Reason for ST Discharge">
          <CheckboxGrid
            options={DISCHARGE_REASON_OPTIONS}
            values={values.dischargeReasons}
            canEdit={canEdit}
            onToggle={(opt) => toggleArray("dischargeReasons", opt)}
            columns={2}
          />
          <div className="mt-2">
            <Field label="Other">
              <input
                type="text"
                className={fieldClass}
                value={values.dischargeReasonOther || ""}
                disabled={!canEdit}
                placeholder="Specify other reason..."
                onChange={(e) =>
                  setValue("dischargeReasonOther", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        <Section icon={MedicalNotesIcon} title="8. Subjective Report">
          <Field label="Patient/Caregiver Statement on Progress">
            <textarea
              rows={4}
              className={`${textareaClass} min-h-[5.5rem]`}
              value={values.subjectiveProgress || ""}
              disabled={!canEdit}
              placeholder="Enter patient's perception of functional gains and current communication/swallowing status..."
              onChange={(e) => setValue("subjectiveProgress", e.target.value)}
            />
          </Field>
        </Section>

        <Section icon={HeartPulseIcon} title="9. Patient Status at Discharge">
          <div className="space-y-2.5">
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Swallowing Status
              </p>
              <PillToggle
                name="swallowingStatus"
                options={SWALLOWING_STATUS_OPTIONS}
                value={values.swallowingStatus || ""}
                canEdit={canEdit}
                onChange={(v) => setValue("swallowingStatus", v)}
              />
            </div>
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Speech Intelligibility
              </p>
              <PillToggle
                name="speechIntelligibility"
                options={SPEECH_INTELLIGIBILITY_OPTIONS}
                value={values.speechIntelligibility || ""}
                canEdit={canEdit}
                onChange={(v) => setValue("speechIntelligibility", v)}
              />
            </div>
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Communication / Expression
              </p>
              <PillToggle
                name="communicationExpression"
                options={COMMUNICATION_EXPRESSION_OPTIONS}
                value={values.communicationExpression || ""}
                canEdit={canEdit}
                onChange={(v) => setValue("communicationExpression", v)}
              />
            </div>
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Cognitive / Mental Status
              </p>
              <PillToggle
                name="cognitiveMentalStatus"
                options={COGNITIVE_MENTAL_OPTIONS}
                value={values.cognitiveMentalStatus || ""}
                canEdit={canEdit}
                onChange={(v) => setValue("cognitiveMentalStatus", v)}
              />
            </div>
          </div>
        </Section>

        <Section icon={CommentsIcon} title="10. Communication Assessment">
          <div className="space-y-3">
            <div>
              <SubHeading>Speech Production</SubHeading>
              <ExclusiveChoiceGrid
                name="speechProductionStatus"
                options={SPEECH_PRODUCTION_STATUS_OPTIONS}
                value={values.speechProductionStatus || ""}
                canEdit={canEdit}
                columns={4}
                onChange={(v) => setValue("speechProductionStatus", v)}
              />
              <div className="mt-1.5">
                <Field label="Findings">
                  <textarea
                    rows={3}
                    className={`${textareaClass} min-h-[4.5rem]`}
                    value={values.speechProductionFindings || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("speechProductionFindings", e.target.value)
                    }
                  />
                </Field>
              </div>
            </div>
            <div>
              <SubHeading>Expressive Language</SubHeading>
              <ExclusiveChoiceGrid
                name="expressiveLanguageStatus"
                options={LANGUAGE_STATUS_OPTIONS}
                value={values.expressiveLanguageStatus || ""}
                canEdit={canEdit}
                columns={4}
                onChange={(v) => setValue("expressiveLanguageStatus", v)}
              />
              <div className="mt-1.5">
                <Field label="Assessment">
                  <textarea
                    rows={3}
                    className={`${textareaClass} min-h-[4.5rem]`}
                    value={values.expressiveLanguageAssessment || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("expressiveLanguageAssessment", e.target.value)
                    }
                  />
                </Field>
              </div>
            </div>
            <div>
              <SubHeading>Receptive Language</SubHeading>
              <ExclusiveChoiceGrid
                name="receptiveLanguageStatus"
                options={LANGUAGE_STATUS_OPTIONS}
                value={values.receptiveLanguageStatus || ""}
                canEdit={canEdit}
                columns={4}
                onChange={(v) => setValue("receptiveLanguageStatus", v)}
              />
              <div className="mt-1.5">
                <Field label="Assessment">
                  <textarea
                    rows={3}
                    className={`${textareaClass} min-h-[4.5rem]`}
                    value={values.receptiveLanguageAssessment || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("receptiveLanguageAssessment", e.target.value)
                    }
                  />
                </Field>
              </div>
            </div>
            <div className="rounded-md border border-indigo-100 p-2.5">
              <SubHeading>Cognitive Communication (Areas Assessed)</SubHeading>
              <CheckboxGrid
                options={COGNITIVE_AREA_OPTIONS}
                values={values.cognitiveAreasAssessed}
                canEdit={canEdit}
                onToggle={(opt) => toggleArray("cognitiveAreasAssessed", opt)}
                columns={3}
              />
              <div className="mt-1.5">
                <Field label="Current Status">
                  <textarea
                    rows={3}
                    className={`${textareaClass} min-h-[4.5rem]`}
                    value={values.cognitiveCurrentStatus || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("cognitiveCurrentStatus", e.target.value)
                    }
                  />
                </Field>
              </div>
            </div>
          </div>
        </Section>

        <Section icon={UtensilsIcon} title="11. Swallowing & Diet Assessment">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <SelectField
              label="Current Solid Consistency"
              name="solidsConsistency"
              value={values.solidsConsistency || ""}
              options={SOLIDS_CONSISTENCY_OPTIONS}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(v) => setValue("solidsConsistency", v)}
            />
            <SelectField
              label="Current Liquid Consistency"
              name="liquidsConsistency"
              value={values.liquidsConsistency || ""}
              options={LIQUIDS_CONSISTENCY_OPTIONS}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(v) => setValue("liquidsConsistency", v)}
            />
          </div>
          <div className="mt-2.5">
            <SubHeading>Swallowing Safety Precautions</SubHeading>
            <CheckboxGrid
              options={SWALLOW_PRECAUTION_OPTIONS}
              values={values.swallowPrecautions}
              canEdit={canEdit}
              onToggle={(opt) => toggleArray("swallowPrecautions", opt)}
              columns={3}
            />
          </div>
        </Section>

        <Section icon={PhoneIcon} title="12. Functional Communication Status">
          <p className="text-[12px] font-semibold text-slate-800 mb-1.5">
            Patient Ability:
          </p>
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full min-w-[520px] text-left">
              <thead>
                <tr className={tableHeadClass}>
                  <th className="px-2.5 py-1.5">Area</th>
                  <th className="px-2.5 py-1.5 w-[28%] text-center">
                    Independent
                  </th>
                  <th className="px-2.5 py-1.5 w-[28%] text-center">
                    Needs Assistance
                  </th>
                </tr>
              </thead>
              <tbody>
                {FUNCTIONAL_ABILITY_ROWS.map((row) => (
                  <tr key={row} className="border-t border-slate-100">
                    <td className="px-2.5 py-1.5 text-[12px] font-medium text-slate-700">
                      {row}
                    </td>
                    {FUNCTIONAL_ABILITY_OPTIONS.map((opt) => (
                      <td key={opt} className="px-2.5 py-1.5 text-center">
                        <input
                          type="checkbox"
                          className={checkboxClass}
                          checked={functional[row] === opt}
                          disabled={!canEdit}
                          onChange={() =>
                            setValues((prev) => ({
                              ...prev,
                              functionalAbility: {
                                ...(prev.functionalAbility || {}),
                                [row]:
                                  prev.functionalAbility?.[row] === opt
                                    ? ""
                                    : opt,
                              },
                            }))
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2">
            <Field label="Additional Comments">
              <textarea
                rows={3}
                className={`${textareaClass} min-h-[4.5rem]`}
                value={values.functionalComments || ""}
                disabled={!canEdit}
                placeholder="Enter additional observations regarding functional communication..."
                onChange={(e) => setValue("functionalComments", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={MedicalNotesIcon} title="13. Hearing & Oral Motor Function">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <SelectField
              label="Hearing Status"
              name="hearingStatus"
              value={values.hearingStatus || ""}
              options={HEARING_STATUS_OPTIONS}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(v) => setValue("hearingStatus", v)}
            />
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Oral Motor Assessment
              </p>
              <PillToggle
                name="oralMotorAssessment"
                options={ORAL_MOTOR_OPTIONS}
                value={values.oralMotorAssessment || ""}
                canEdit={canEdit}
                onChange={(v) => setValue("oralMotorAssessment", v)}
              />
            </div>
          </div>
        </Section>

        <Section icon={HeartPulseIcon} title="14. Pain Assessment">
          <div className="rounded-md border border-indigo-100 bg-indigo-50/30 p-2.5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
              <PainScalePills
                label="Pain Score (0–10)"
                name="painLevel"
                value={values.painLevel}
                canEdit={canEdit}
                onChange={(v) => setValue("painLevel", v)}
              />
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                  Impact on ST Participation
                </p>
                <PillToggle
                  name="painImpact"
                  options={PAIN_IMPACT_OPTIONS}
                  value={values.painImpact || ""}
                  canEdit={canEdit}
                  onChange={(v) => setValue("painImpact", v)}
                />
              </div>
            </div>
          </div>
        </Section>

        <Section icon={TargetIcon} title="15. ST Goals Status">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full min-w-[560px] text-left">
              <thead>
                <tr className={tableHeadClass}>
                  <th className="px-2.5 py-1.5">Goal Area</th>
                  {GOAL_STATUS_OPTIONS.map((opt) => (
                    <th key={opt} className="px-2.5 py-1.5 w-[20%] text-center">
                      {opt}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ST_GOAL_ROWS.map((row) => (
                  <tr key={row} className="border-t border-slate-100">
                    <td className="px-2.5 py-1.5 text-[12px] font-medium text-slate-700">
                      {row}
                    </td>
                    {GOAL_STATUS_OPTIONS.map((opt) => (
                      <td key={opt} className="px-2.5 py-1.5 text-center">
                        <input
                          type="radio"
                          name={`goal-${row}`}
                          className={checkboxClass}
                          checked={goals[row] === opt}
                          disabled={!canEdit}
                          onChange={() =>
                            setValues((prev) => ({
                              ...prev,
                              goalStatuses: {
                                ...(prev.goalStatuses || {}),
                                [row]: opt,
                              },
                            }))
                          }
                          onClick={() => {
                            if (!canEdit) return;
                            if (goals[row] === opt) {
                              setValues((prev) => ({
                                ...prev,
                                goalStatuses: {
                                  ...(prev.goalStatuses || {}),
                                  [row]: "",
                                },
                              }));
                            }
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2">
            <Field label="Goal Progress Summary">
              <textarea
                rows={4}
                className={`${textareaClass} min-h-[5.5rem]`}
                value={values.goalProgressSummary || ""}
                disabled={!canEdit}
                placeholder="Enter clinical narrative regarding goal achievement and functional progress..."
                onChange={(e) =>
                  setValue("goalProgressSummary", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        <Section
          icon={BrainIcon}
          title="16. Skilled ST Interventions Completed During Episode"
        >
          <CheckboxGrid
            options={ST_INTERVENTION_OPTIONS}
            values={values.interventionsCompleted}
            canEdit={canEdit}
            onToggle={(opt) => toggleArray("interventionsCompleted", opt)}
            columns={3}
          />
        </Section>

        <Section icon={GraduationCapIcon} title="17. Education & Home Exercise Program">
          <div className="space-y-2.5">
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Education Provided
              </p>
              <ChipMulti
                options={EDUCATION_TOPIC_OPTIONS}
                values={values.educationTopics}
                canEdit={canEdit}
                onToggle={(opt) => toggleArray("educationTopics", opt)}
              />
            </div>
            <SelectField
              label="HEP Compliance Status"
              name="hepCompliance"
              value={values.hepCompliance || ""}
              options={HEP_COMPLIANCE_OPTIONS}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(v) => setValue("hepCompliance", v)}
            />
          </div>
        </Section>

        <Section icon={LogOutIcon} title="18. Discharge Narrative & Plan">
          <Field label="ST Discharge Summary Narrative">
            <textarea
              rows={5}
              className={`${textareaClass} min-h-[6.5rem]`}
              value={values.dischargeNarrative || ""}
              disabled={!canEdit}
              placeholder="Synthesize the entire course of treatment, highlighting functional improvements in communication and safety..."
              onChange={(e) => setValue("dischargeNarrative", e.target.value)}
            />
          </Field>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <SelectField
              label="Discharge Disposition"
              name="dischargeDisposition"
              value={values.dischargeDisposition || ""}
              options={DISCHARGE_DISPOSITION_OPTIONS}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(v) => setValue("dischargeDisposition", v)}
            />
            <Field label="Follow-up Care Recommendation">
              {printMode ? (
                <div className={`${fieldClass} flex items-center`}>
                  {values.followUpRecommendation || "—"}
                </div>
              ) : (
                <input
                  type="text"
                  className={fieldClass}
                  value={values.followUpRecommendation || ""}
                  disabled={!canEdit}
                  placeholder="e.g., Annual swallowing reassessment"
                  onChange={(e) =>
                    setValue("followUpRecommendation", e.target.value)
                  }
                />
              )}
            </Field>
          </div>
        </Section>

        <Section icon={FileCheckIcon} title="19. Physician/Care Team Communication">
          <Field label="Communication Details">
            <textarea
              rows={4}
              className={`${textareaClass} min-h-[5.5rem]`}
              value={values.physicianCommunication || ""}
              disabled={!canEdit}
              placeholder="Summary of communication with Primary Physician regarding final discharge status..."
              onChange={(e) =>
                setValue("physicianCommunication", e.target.value)
              }
            />
          </Field>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          canEdit={canEdit}
          canSign={canSign}
          printMode={printMode}
          values={values}
          onPatientSign={signatures.handlePatientSign}
          onClinicianSign={handleClinicianSign}
          onChange={setValues}
          clinicianLabel="Clinician Digital Signature"
          clinicianSectionTitle="Clinician Digital Signature"
        />

        {isReviewer ? (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        ) : values.reviewComments ? (
          <OfficeCommentsNote comments={values.reviewComments} />
        ) : null}

        {!printMode &&
          (permissions.canSendToOffice ||
            permissions.canToBeSign ||
            permissions.canReview ||
            permissions.canEdit) && (
            <FormActionBar
              permissions={permissions}
              status={form.status}
              canEdit={canEdit}
              saving={saving}
              onSubmit={submit}
            />
          )}
      </div>
    </div>
  );
};

StDischargeNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default StDischargeNoteForm;
